<template>
    <div class="p-consultations">
        <div class="card">
            <div class="card-header">{{ $locale.consultation_host }}予約リスト</div>

            <div class="card-body p-consultations__content">
                <div :class="{ 'u-loader u-loader--p5' : ! consultations }">
                    <div v-if="consultations">
                        <template v-if="consultations.length > 0">
                            <div class="d-flex flex-wrap mb-1">
                                <div v-if="hasReservedByDoctor" class="reservedNotice small mr-2">
                                    <div></div>
                                    <div>&nbsp;:&nbsp;医師による予約</div>
                                </div>
                                <div v-if="hasRepeatReserved" class="d-flex align-items-center small">
                                    <div class="reservedLabel">再</div>
                                    <div>&nbsp;:&nbsp;再{{ $locale.consultation_host }}</div>
                                </div>
                            </div>

                            <b-table
                                hover
                                :responsive="true"
                                :tbody-tr-class="rowClass"
                                :items="consultations"
                                :fields="fields"
                                :sort-by.sync="$store.state.userConsultations.sortBy"
                                :sort-desc.sync="$store.state.userConsultations.sortDesc"
                                @sort-changed="fetchConsultations">
                                <template v-slot:cell(id)="data">
                                    <div
                                        :class="{ 'reservedByDoctor' : Number( data.item.flg_reserved_by_doctor ) === 1 }">
                                        {{ data.item.id }}
                                    </div>
                                </template>

                                <template v-slot:cell(start_time)="data">
                                    <div class="consultationTime">
                                        <span>
                                            {{ helper.dateForHuman( data.item.start_time, user.timezone, { exceptYear: helper.isCurrentYear(data.item, user.timezone), simpleYear: true }) }}
                                        </span>
                                        <span :class="helper.dayForHumanCSS( data.item.start_time , user.timezone )">
                                            {{ helper.dayForHuman(data.item.start_time, user.timezone, { bracket: true }) }}
                                        </span>
                                        <span class="ml-2">
                                            {{ helper.timeForHuman( data.item , user.timezone ) }}
                                        </span>
                                        <span v-if="data.item.doctor_menu !== null">
                                            {{ `（${data.item.doctor_menu.consultation_minutes}分）` }}
                                        </span>
                                    </div>
                                    <timezone-box-jst
                                        v-if="user.timezone !== 'Asia/Tokyo'"
                                        :consultation="data.item"
                                        :timezone="user.timezone"
                                        except-year>
                                    </timezone-box-jst>
                                </template>

                                <template v-slot:cell(doctors.full_name_kana)="data">
                                    <template v-if="data.item.doctor">
                                        <a href="#" @click="showDoctorModal(data.item.doctor_id, $event)">
                                            {{ data.item.doctor.full_name }}
                                        </a>
                                        <div class="small">{{ data.item.doctor.full_name_kana }}</div>
                                    </template>
                                    <template v-else-if="data.item.doctor_id === null">
                                        <span class="text-muted">未定</span>
                                    </template>
                                    <template v-else>
                                        <span class="text-muted">退会済の医師</span>
                                    </template>
                                </template>

                                <template v-slot:cell(medical_courses.name)="data">
                                    <template v-if="data.item.doctor_menu">
                                        <div style="line-height:1; margin-top:3px;">
                                            {{ data.item.medical_course.name }}
                                        </div>
                                        <div
                                            v-if="data.item.doctor_menu.is_health_package"
                                            class="doctorMenuHealthPackageIcon">
                                            <img src="/images/common/label_health_package1.png">
                                        </div>
                                        <a href="#" @click="showModalDoctorMenu(data.item.doctor_menu_id, data.item)">
                                            {{ data.item.doctor_menu.title }}
                                        </a>
                                        <div
                                            v-if="data.item.is_repeat_reserved"
                                            class="reservedLabel">
                                            再
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div>{{ data.item.medical_course.name }}</div>
                                    </template>
                                </template>

                                <template v-slot:cell(status)="data">
                                    <div class="c-indicator" :class="detectStatusCSS( data.item )">
                                        <span>{{ detectStatusName(data.item) }}</span>
                                        <span>/</span>
                                        <span>{{ detectStatusNameMedicalRecord(data.item) }}</span>
                                    </div>
                                    <div class="d-flex mt-1">
                                        <div
                                            v-show="data.item.messages_count > 0"
                                            class="c-counter-badge mr-2">
                                            <b-icon-chat-dots-fill></b-icon-chat-dots-fill>
                                        </div>
                                        <div
                                            v-show="data.item.images_allowed_count > 0"
                                            class="c-counter-badge mr-2">
                                            <img src="/images/common/icon_camera2_bk.svg" style="width: 14px; position: relative; top: -1px;">
                                            <span class="c-counter-badge__item">{{ data.item.images_allowed_count }}</span>
                                        </div>
                                        <div
                                            v-show="data.item.attached_images_allowed_count > 0"
                                            class="c-counter-badge">
                                            <b-icon-images></b-icon-images>
                                            <span class="c-counter-badge__item">{{ data.item.attached_images_allowed_count }}</span>
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:cell(invoice)="data">
                                    <div>
                                        <div
                                            v-if="data.item.doctor_menu && data.item.doctor_menu.is_health_package"
                                            class="text-muted">
                                            -
                                        </div>
                                        <div
                                            v-else-if="data.item.is_force_paid"
                                            class="text-muted">
                                            支払済
                                        </div>
                                        <div v-else-if="data.item.invoice">
                                            <span
                                                class="u-palt"
                                                :class="{ 'is-invalid' : invoiceErrorStatus.includes(data.item.invoice.status) }">
                                                {{ detectInvoiceStatusName(data.item.invoice) }}
                                            </span>
                                            <a
                                                v-if="!isGroupUser || isUserAuthorizerOrganization || isUserAuthorizerCompany"
                                                :href="authorizerUrl"
                                                class="d-block small text-muted linkInvoice">
                                                請求ID:&nbsp;#{{ data.item.invoice.id }}
                                            </a>
                                            <div
                                                v-if="data.item.payment_method && !canSkipPayment(data.item)"
                                                class="small text-muted">
                                                {{ data.item.payment_method.name }}
                                            </div>
                                        </div>
                                        <div
                                            v-else
                                            class="text-muted">
                                            <div>未請求</div>
                                            <div
                                                v-if="data.item.payment_method && !canSkipPayment(data.item)"
                                                class="small text-muted">
                                                {{ data.item.payment_method.name }}
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:cell(links)="data">
                                    <div class="d-flex justify-content-end">
                                        <b-button
                                            v-if="canEnterRoom(data.item.status)"
                                            size="sm"
                                            variant="yokumiru-doctor"
                                            @click="onClickEnterRoom(data.item)">
                                            {{ $locale.consultation_host }}室
                                        </b-button>
                                    </div>
                                </template>
                            </b-table>

                            <div class="d-flex flex-wrap justify-content-between align-items-center">
                                <b-pagination
                                    :total-rows="$store.state.userConsultations.total"
                                    :per-page="$store.state.userConsultations.per_page"
                                    v-model="$store.state.userConsultations.current_page"
                                    @change="fetchConsultations">
                                </b-pagination>

                                <div class="mb-3 mr-2">
                                    <span class="small">{{ dispPaginationDetail }}</span>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <p class="text-muted mb-0">予約はまだ1件もありません。</p>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <consultation-detail-modal :user="user"/>
        <doctor-menu-modal user-type="user"/>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import constants from '../../constants'
import YokumiruHelper from '../../YokumiruHelper'
import moment from 'moment-timezone'

export default {
    props: {
        data: {
            type: Object,
            default: null,
        },
        user: {
            type: Object,
            default: null,
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            consultations: null,
            consultationStatus: constants.consultations,
            invoiceStatus: constants.invoices,
            invoiceErrorStatus: constants.invoices.errors,
            paginationConf: {
                currentPage: 1,
                perPage: 10,
                totalRows: 0,
                from: 0,
                to: 0,
            },
            sortBy: 'start_time',
            sortDesc: true,
            fields: [
                {
                    key: 'id',
                    label: '#',
                    sortable: true,
                },
                {
                    key: 'start_time',
                    label: '予約日時',
                    sortable: true,
                },
                {
                    key: 'doctors.full_name_kana',
                    label: '医師',
                    sortable: true,
                },
                {
                    key: 'medical_courses.name',
                    label: `${this.$locale.consultation_host}科・メニュー`,
                    sortable: true,
                },
                {
                    'key': 'status',
                    'label': 'ステータス',
                    sortable: true,
                },
            ],
        }
    },

    created: function () {
        this.initQueryFromState()
        this.fetchConsultations()
        this.fetchRecentlyConsultations()

        if (this.$store.state.configs.payment == 1) {
            // グループ所属していない or 法人の担当者
            if (!this.helper.isGroupUser(this.user) || (this.userCompany && this.userCompany.authorizer_id === this.user.id)) {
                this.fields.push({
                    key: 'invoice',
                    label: '請求',
                    sortable: false,
                })
            }
        }
        this.fields.push({
            key: 'links',
            label: '',
            class: 'text-right',
            sortable: false,
        })

        // 3DS認証が必要な予約の場合、Consultation を Stripe webhook 経由で restore しているため wait を設ける
        bus.$on('user-reservation-completed', () => { setTimeout(() => this.fetchConsultations(), 2000) })
        bus.$on('user-draft-reservation-accepted', () => { setTimeout(() => this.fetchConsultations(), 2000) })
        bus.$on('user-reservation-canceled', this.fetchConsultations)
    },

    beforeDestroy: function () {
        bus.$off('user-reservation-completed')
        bus.$off('user-reservation-canceled')
        bus.$off('user-draft-reservation-accepted')
    },

    methods: {
        ...mapMutations({
            setQuery: 'userConsultations/setQuery',
            setQueries: 'userConsultations/setQueries',
            setRecentlyConsultations: 'userConsultations/setRecentlyConsultations',
        }),

        /**
         * 診療一覧 tr クラス
         */
        rowClass: function (item, type) {
            if (item && type === 'row') {
                if (item.invoice && this.invoiceErrorStatus.includes(item.invoice.status)) {
                    return 'is-invalid'
                }
            }
            return null
        },

        /**
         * 無料かどうか
         *
         * @param {object} consultation
         * @return {boolean}
         */
        canSkipPayment: function (consultation) {
            return this.$locale.type === 'consul' && consultation.estimated_total <= 0
        },

        /**
         * 請求ステータス表記
         *
         * @param {object} invoice
         * @return {string}
         */
        detectInvoiceStatusName: function (invoice) {
            let result = this.invoiceStatus.all.find(status => {
                return status.value === invoice.status
            })

            if (result && this.$store.state.configs.subscription != 1) {
                if (result.value === constants.invoices.authorized
                    && invoice.send_at
                    && invoice.payment_method_id !== constants.payment_methods.credit) {
                    return '請求済'
                }
            }

            return result ? result.text : ''
        },

        /**
         * 利用制限判定（入室）
         *
         * @return {boolean}
         */
        isRoomRestricted: function () {
            return this.isUserPausedCompany
        },

        /**
         * 検索条件の初期化
         */
        initQueryFromState: function () {
            if (localStorage.getItem('yokumiruWeb')) {
                const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                if (data.userConsultations) {
                    this.setQueries(data.userConsultations)
                }
            }
        },

        /**
         * 直近の相談予約を保持
         * @see https://github.com/sewii/Yokumiru/issues/568
         */
        fetchRecentlyConsultations: function () {
            const endpoint = constants.user.consultations.endpoint.base
            const query = `?page=1&sort=start_time&desc=true&status=${this.consultationStatus.finished}`

            axios.get(`${endpoint}${query}`)
                .then(({data}) => {
                    this.setRecentlyConsultations(data.data)
                })
        },

        /**
         * 相談予約一覧
         *
         * @param {number|object} param
         */
        fetchConsultations: async function (param = null) {
            this.consultations = null

            if (Number.isInteger(param) && param > 0) {
                await this.setQuery({ key: 'current_page', value: param })
            }
            else if (param !== null && param.hasOwnProperty('sortBy')) {
                await this.setQuery({ key: 'sortBy', value: param.sortBy })
                await this.setQuery({ key: 'sortDesc', value: param.sortDesc })
            }

            const endpoint = constants.user.consultations.endpoint.base

            axios.get(`${endpoint}${this.buildQuery()}`)
                .then(({data}) => {
                    this.consultations = data.data
                    this.setQueries(data.meta)
                    bus.$emit('user-consultations-fetched', this.consultations.length)
                })
        },

        /**
         * QueryString生成
         *
         * @return {string}
         */
        buildQuery: function () {
            let query = '?'

            const state = this.$store.state.userConsultations
            query += `page=${state.current_page}`
            query += `&sort=${state.sortBy}`
            query += `&desc=${state.sortDesc}`

            return query
        },

        /**
         * 医師詳細モーダル表示
         *
         * @param {number} doctorId
         * @param {object} e
         */
        showDoctorModal: function (doctorId, e) {
            e.preventDefault()
            bus.$emit('open-modal-doctor-profile', doctorId, { canReserve: true })
        },

        /**
         * ステータス表記（相談記録）
         *
         * @param {object} consultation
         * @return {string}
         */
        detectStatusNameMedicalRecord: function (consultation) {
            if (consultation.medical_record && consultation.medical_record.published_at) {
                return '記録公開済'
            }
            return '記録未公開'
        },

        /**
         * ステータス表記（相談予約）
         *
         * @param {object} consultation
         * @return {string}
         */
        detectStatusName: function (consultation) {
            let result = this.consultationStatus.all.find(status => {
                return status.value === consultation.status
            })

            if (this.$locale.type === 'consul') {
                result.text = result.text.replace('診療', '相談')
                result.text = result.text.replace('患者', '会員')
            }

            // キャンセル
            if (consultation.flg_cancel == 1) {
                if (constants.consultations.cancelAll.includes(result.value)) {
                    return result.text
                }
                if (consultation.flg_cancel_by_doctor == 1) {
                    return 'キャンセル（医師）'
                }
                return this.$locale.type === 'consul' ? 'キャンセル（会員）' : 'キャンセル（患者）'
            }

            // その他
            return result.text
        },

        /**
         * ステータスCSS（相談予約）
         *
         * @param {object} consultation
         * @return {string}
         */
        detectStatusCSS: function (consultation) {
            const result = this.consultationStatus.all.find(status => {
                return status.value === consultation.status
            })
            return result.css
        },

        /**
         * 相談室の入室可能判定
         *
         * @param {number} status
         * @return {boolean}
         */
        canEnterRoom: function (status) {
            return constants.consultations.canEnterAll.includes(status)
        },

        /**
         * 相談室ボタン押下時
         *
         * @param {object} consultation
         */
        onClickEnterRoom: function (consultation) {
            if (this.isRoomRestricted()) {
                this.openModalRestrict()
            }
            else {
                location.href = `/consultations/${consultation.id}`
            }
        },

        /**
         * 利用制限モーダルを表示
         */
        openModalRestrict: function () {
            if (this.restrictedBy === 'organization') {
                bus.$emit(
                    'open-modal-page-transition',
                    '入室できません',
                    '会員様の団体は現在利用停止中です。'
                )
            }
            else if (this.restrictedBy === 'company') {
                bus.$emit(
                    'open-modal-page-transition',
                    '入室できません',
                    '会員様の法人は現在利用停止中です。'
                )
            }
            else if (this.restrictedBy === 'canceled') {
                // プラン解約時に有効期限以降の予約は自動キャンセルされるため、通常は通らない
                bus.$emit(
                    'open-modal-page-transition',
                    '入室できません',
                    'サービスの有効期限を過ぎています（予約キャンセルをご希望の場合はサポートセンターまでお問い合わせください）。'
                )
            }
            else {
                bus.$emit(
                    'open-modal-page-transition',
                    '入室できません',
                    '未決済のお支払いがあります。入室前にお支払いを完了させてください。'
                )
            }
        },

        /**
         * 医師メニュー詳細モーダル表示
         *
         * @param {number} doctorMenuId
         * @param {object} consultation
         */
        showModalDoctorMenu: function (doctorMenuId, consultation) {
            bus.$emit('open-modal-doctor-menu', doctorMenuId, consultation)
        },
    },

    computed: {
        /**
         * 所属法人
         * @return {Object|undefined}
         */
        userCompany: function () {
            return this.helper.userCompany(this.user)
        },

        /**
         * 法人会員で利用停止中かどうか
         * @return {boolean}
         */
        isUserPausedCompany: function () {
            return this.userCompany && this.userCompany.flg_paused
        },

        /**
         * 法人の担当者かどうか
         * @return {boolean}
         */
        isUserAuthorizerCompany: function () {
            if (!this.user) return false

            return !this.userOrganization
                && this.userCompany
                && this.userCompany.authorizer_id === this.user.id
        },

        /**
         * 所属団体
         * @return {Object|undefined}
         */
        userOrganization: function () {
            return this.helper.userOrganization(this.user)
        },

        /**
         * 団体会員で利用停止中かどうか
         * @return {boolean}
         */
        isUserPausedOrganization: function () {
            return this.userOrganization && this.userOrganization.flg_paused
        },

        /**
         * 団体の担当者かどうか
         * @return {boolean}
         */
        isUserAuthorizerOrganization: function () {
            if (!this.user) return false

            return this.userOrganization
                && this.userOrganization.authorizer_id === this.user.id
        },

        /**
         * 担当者画面のURL
         * @return {string}
         */
        authorizerUrl: function () {
            if (this.userOrganization) return '/organization'
            if (this.userCompany) return '/company'
            return '/invoices'
        },

        /**
         * 利用制限が何によってかけられているか
         * @return {string|null}
         */
        restrictedBy: function () {
            if (this.isUserPausedOrganization) {
                return 'organization'
            }
            else if (this.isUserPausedCompany) {
                return 'company'
            }
            return null
        },

        /**
         * グループに所属しているかどうか
         * @return {boolean}
         */
        isGroupUser: function () {
            return this.helper.isGroupUser(this.user)
        },

        /**
         * 医師による予約の存在判定
         * @return {boolean}
         */
        hasReservedByDoctor: function () {
            if (!this.consultations) return false

            return !!this.consultations.find(consultation => {
                return Number(consultation.flg_reserved_by_doctor) === 1
            })
        },

        /**
         * 再相談予約の存在判定
         * @return {boolean}
         */
        hasRepeatReserved: function () {
            if (!this.consultations) return false

            return !!this.consultations.find(consultation => {
                return Number(consultation.is_repeat_reserved) === 1
            })
        },

        /**
         * ページング詳細表示
         * @returns {string}
         */
        dispPaginationDetail: function () {
            if (this.paginationConf.totalRows < 1) {
                return '';
            }

            return `全${this.paginationConf.totalRows}件中 ${this.paginationConf.from}～${this.paginationConf.to}件表示`;
        },
    },
}
</script>

<style lang="scss" scoped>
.reservedLabel {
    position: relative;
    top: -1px;
    display: inline-block;
    width: 18px;
    height: 18px;
    line-height: 18px;
    color: #fff;
    font-size: 10px;
    text-align: center;
    background-color: #e7a222;
    border-radius: 50%;
}

.consultationTime {
    color: #464a4d;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.4;
    font-feature-settings: 'palt';

    span:last-child {
        color: #6e7275;
        font-weight: normal;
    }
}

.linkInvoice {
    text-decoration: underline;

    &:hover {
        text-decoration: none;
    }
}

.doctorMenuHealthPackageIcon {
    margin: 3px 0 1px;

    img {
        width: 130px;
    }
}

.reservedNotice {
    display: flex;
    align-items: center;

    div:nth-child(1) {
        width: 16px;
        height: 12px;
        background: #244787;
        border-radius: 4px;
    }

    div:nth-child(2) {
        font-size: .7rem;
    }
}

.reservedByDoctor {
    display: inline-block;
    padding: 0 4px;
    color: #fff;
    background: #417daf;
    border-radius: 4px;
}

.status-reserved {
    display: flex;
    align-items: center;
    margin: 0 0 5px 10px;

    div:nth-child(1) {
        width: 10px;
        height: 10px;
        background: #417daf;
        border-radius: 5px;
    }

    div:nth-child(2) {
        font-size: .7rem;
    }
}

.status-hold {
    display: flex;
    align-items: center;
    margin: 0 0 5px 10px;

    div:nth-child(1) {
        width: 10px;
        height: 10px;
        background: #2fa69a;
        border-radius: 5px;
    }

    div:nth-child(2) {
        font-size: .7rem;
    }
}

.status-finished {
    display: flex;
    align-items: center;
    margin: 0 0 5px 10px;

    div:nth-child(1) {
        width: 10px;
        height: 10px;
        background: #98a6ad;
        border-radius: 5px;
    }

    div:nth-child(2) {
        font-size: .7rem;
    }
}

.status-canceled {
    display: flex;
    align-items: center;
    margin: 0 0 5px 10px;

    div:nth-child(1) {
        width: 10px;
        height: 10px;
        background: #ef7320;
        border-radius: 5px;
    }

    div:nth-child(2) {
        font-size: .7rem;
    }
}
</style>
